// src/operator/console/clients-console.tsx
//
// Console de gestion multi-tenant : clients MSP, sites, partages
// inter-tenants (propriétaire + invités) et grants RBAC (délégation
// tenant / client / site / host).
//
// Branchée sur les API backend : /api/clients, /api/clients/:id/sites,
// /api/clients/:id/shares, /api/grants, /tenants.

import { createSignal, createMemo, For, Show } from "solid-js";
import { createQuery } from "@tanstack/solid-query";
import { useConfirm } from "../shared/confirm-dialog";
import PolicyEditor, { type Preset } from "../panels/policy-editor";
import type { PolicyObject } from "../shared/host-policy-schema";

interface Tenant {
  id: string;
  name: string;
  slug: string;
}
interface Client {
  id: string;
  ownerTenantId: string;
  name: string;
  slug: string;
  createdAt: string;
  policy?: PolicyObject;
}
interface Site {
  id: string;
  clientId: string;
  name: string;
  createdAt: string;
  policy?: PolicyObject;
}
interface Share {
  clientId: string;
  tenantId: string;
  accessLevel: string;
  grantedAt: string;
}
interface Grant {
  id: string;
  userId: string;
  scopeType: string;
  scopeId: string;
  role: string;
  expiresAt: string | null;
  grantedAt: string;
}
interface Operator {
  id: string;
  email: string;
  name: string;
  roles: string[];
  tenantId: string | null;
}
interface HostRow {
  hostId: string;
  hostname: string;
  status?: string;
  clientId: string | null;
  siteId: string | null;
  clientLabel: string | null;
  siteLabel: string | null;
}
interface InstallToken {
  id: string;
  tenant_id: string;
  label: string;
  created_by_user_id: string | null;
  allowed_domain: string | null;
  enrollment_policy: unknown;
  expires_at: string | null;
  used_at: string | null;
  used_by_host_id: string | null;
  revoked_at: string | null;
  revoked_by_user_id: string | null;
  created_at: string;
  client_id: string | null;
  site_id: string | null;
  install_key: string | null;
  usage_mode: "single" | "multiple";
  use_count: number;
}

async function jget<T>(url: string): Promise<T> {
  const r = await fetch(url, { credentials: "include" });
  if (!r.ok) throw new Error(`${url} → ${r.status}`);
  return r.json() as Promise<T>;
}

async function jsend(url: string, method: string, body?: unknown): Promise<Record<string, unknown>> {
  const r = await fetch(url, {
    method,
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const data = (await r.json().catch(() => ({}))) as Record<string, unknown>;
  if (!r.ok) throw new Error((data?.error as string) || `HTTP ${r.status}`);
  return data;
}

const SCOPE_TYPES = ["tenant", "client", "site", "host"];
const RBAC_ROLES = ["viewer", "operator", "manager"];
const ACCESS_LEVELS = ["read_only", "operate"];

export function ClientsConsolePage() {
  const confirmDialog = useConfirm();
  const [err, setErr] = createSignal<string | null>(null);
  const [selectedClientId, setSelectedClientId] = createSignal<string | null>(null);

  const clientsQ = createQuery<Client[]>(() => ({
    queryKey: ["clients"],
    queryFn: () => jget<{ clients: Client[] }>("/api/clients").then((d) => d.clients || []),
  }));
  const tenantsQ = createQuery<Tenant[]>(() => ({
    queryKey: ["tenants"],
    queryFn: () => jget<{ tenants: Tenant[] }>("/tenants").then((d) => d.tenants || []),
  }));
  const grantsQ = createQuery<Grant[]>(() => ({
    queryKey: ["grants"],
    queryFn: () => jget<{ grants: Grant[] }>("/api/grants").then((d) => d.grants || []),
  }));
  const operatorsQ = createQuery<Operator[]>(() => ({
    queryKey: ["operators"],
    queryFn: () => jget<{ operators: Operator[] }>("/api/operators").then((d) => d.operators || []),
  }));
  const hostsQ = createQuery<HostRow[]>(() => ({
    queryKey: ["hosts-assign"],
    queryFn: () => jget<{ items?: HostRow[] }>("/host/list").then((d) => d.items || []),
  }));
  const allSitesQ = createQuery<Site[]>(() => ({
    queryKey: ["all-sites"],
    queryFn: () => jget<{ sites: Site[] }>("/api/sites").then((d) => d.sites || []),
  }));
  const sitesQ = createQuery<Site[]>(() => ({
    queryKey: ["sites", selectedClientId()],
    get enabled() {
      return !!selectedClientId();
    },
    queryFn: () => jget<{ sites: Site[] }>(`/api/clients/${selectedClientId()}/sites`).then((d) => d.sites || []),
  }));
  const sharesQ = createQuery<Share[]>(() => ({
    queryKey: ["shares", selectedClientId()],
    get enabled() {
      return !!selectedClientId();
    },
    queryFn: () => jget<{ shares: Share[] }>(`/api/clients/${selectedClientId()}/shares`).then((d) => d.shares || []),
  }));

  const tenantName = (id: string) => tenantsQ.data?.find((t) => t.id === id)?.name || id.slice(0, 8);
  const clientName = (id: string) => clientsQ.data?.find((c) => c.id === id)?.name || id.slice(0, 8);
  const siteName = (id: string) => allSitesQ.data?.find((s) => s.id === id)?.name || id.slice(0, 8);
  const operatorName = (id: string) => {
    const o = operatorsQ.data?.find((op) => op.id === id);
    return o ? o.name || o.email : id.slice(0, 13) + "…";
  };
  const selectedClient = createMemo(() => clientsQ.data?.find((c) => c.id === selectedClientId()) || null);

  async function run(fn: () => Promise<unknown>, refetch: Array<{ refetch: () => void }>) {
    setErr(null);
    try {
      await fn();
      for (const q of refetch) q.refetch();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }

  // ── États de formulaire ──
  const [newClientName, setNewClientName] = createSignal("");
  const [newClientTenant, setNewClientTenant] = createSignal("");
  const [newSiteName, setNewSiteName] = createSignal("");
  const [shareTenant, setShareTenant] = createSignal("");
  const [shareLevel, setShareLevel] = createSignal("read_only");
  const [grantUser, setGrantUser] = createSignal("");
  const [grantScopeType, setGrantScopeType] = createSignal("client");
  const [grantScopeId, setGrantScopeId] = createSignal("");
  const [grantRole, setGrantRole] = createSignal("operator");
  // ── Nouveau opérateur ──
  const [newOpEmail, setNewOpEmail] = createSignal("");
  const [newOpName, setNewOpName] = createSignal("");
  const [newOpPassword, setNewOpPassword] = createSignal("");
  const [newOpRole, setNewOpRole] = createSignal("support");
  // ── Install token ──
  const [itTenant, setItTenant] = createSignal("");
  const [itScope, setItScope] = createSignal<"general" | "client" | "site">("general");
  const [itClient, setItClient] = createSignal("");
  const [itSite, setItSite] = createSignal("");
  const [itLabel, setItLabel] = createSignal("");
  const [itTtl, setItTtl] = createSignal("60");
  const [itPermanent, setItPermanent] = createSignal(false);
  const [itUsageMode, setItUsageMode] = createSignal<"single" | "multiple">("single");
  const [itResult, setItResult] = createSignal<{
    token: string;
    expiresAt: string | null;
    permanent: boolean;
    usageMode: "single" | "multiple";
    installUrl: string;
    zipInstallUrl: string;
    linuxInstallUrl: string;
    linuxCurl: string;
    windowsUpdaterUrl: string;
    windowsPowerShell: string;
    macCurl: string;
  } | null>(null);
  const installTokensQ = createQuery<InstallToken[]>(() => ({
    queryKey: ["install-tokens", itTenant()],
    get enabled() {
      return !!itTenant();
    },
    queryFn: () =>
      jget<{ tokens: InstallToken[] }>(`/tenants/${itTenant()}/install-tokens`).then((d) => d.tokens || []),
  }));
  // ── Sous-onglets de la page (réduit la densité visuelle) ──
  type SubTab = "clients" | "operators" | "assignments" | "tokens";
  const [tab, setTab] = createSignal<SubTab>("clients");
  const SUB_TABS: Array<{ id: SubTab; label: string }> = [
    { id: "clients", label: "Clients & sites" },
    { id: "operators", label: "Opérateurs & RBAC" },
    { id: "assignments", label: "Affectation hosts" },
    { id: "tokens", label: "Install tokens" },
  ];

  function createClient() {
    const name = newClientName().trim();
    if (!name) return;
    const tenantId = newClientTenant() || undefined;
    run(() => jsend("/api/clients", "POST", { name, ownerTenantId: tenantId }), [clientsQ]).then(() =>
      setNewClientName(""),
    );
  }
  function deleteClient(id: string) {
    run(() => jsend(`/api/clients/${id}`, "DELETE"), [clientsQ]).then(() => {
      setSelectedClientId((current) => (current === id ? null : current));
    });
  }
  function createSite() {
    const cid = selectedClientId();
    const name = newSiteName().trim();
    if (!cid || !name) return;
    run(() => jsend(`/api/clients/${cid}/sites`, "POST", { name }), [sitesQ]).then(() => setNewSiteName(""));
  }
  function deleteSite(siteId: string) {
    run(() => jsend(`/api/sites/${siteId}`, "DELETE"), [sitesQ]);
  }
  function addShare() {
    const cid = selectedClientId();
    const tenantId = shareTenant();
    const accessLevel = shareLevel();
    if (!cid || !tenantId) return;
    run(() => jsend(`/api/clients/${cid}/shares`, "POST", { tenantId, accessLevel }), [sharesQ]).then(() =>
      setShareTenant(""),
    );
  }
  function removeShare(tenantId: string) {
    const cid = selectedClientId();
    if (!cid) return;
    run(() => jsend(`/api/clients/${cid}/shares/${tenantId}`, "DELETE"), [sharesQ]);
  }
  function createGrant() {
    const userId = grantUser().trim();
    const scopeId = grantScopeId().trim();
    const scopeType = grantScopeType();
    const role = grantRole();
    if (!userId || !scopeId) return;
    run(
      () =>
        jsend("/api/grants", "POST", {
          userId,
          scopeType,
          scopeId,
          role,
        }),
      [grantsQ],
    ).then(() => {
      setGrantUser("");
      setGrantScopeId("");
    });
  }
  function deleteGrant(id: string) {
    run(() => jsend(`/api/grants/${id}`, "DELETE"), [grantsQ]);
  }
  function assignOperatorTenant(userId: string, tenantId: string, currentTenantId: string | null) {
    if (tenantId) {
      run(() => jsend(`/tenants/${tenantId}/operators/${userId}`, "PUT"), [operatorsQ]);
    } else if (currentTenantId) {
      run(() => jsend(`/tenants/${currentTenantId}/operators/${userId}`, "DELETE"), [operatorsQ]);
    }
  }
  function assignHostClient(hostId: string, clientId: string) {
    // Changer de client réinitialise le site (il appartenait à l'ancien client).
    run(() => jsend(`/host/${hostId}`, "PATCH", { clientId: clientId || null, siteId: null }), [hostsQ]);
  }
  function assignHostSite(hostId: string, siteId: string) {
    run(() => jsend(`/host/${hostId}`, "PATCH", { siteId: siteId || null }), [hostsQ]);
  }
  function createOperator() {
    const email = newOpEmail().trim();
    const name = newOpName().trim();
    const password = newOpPassword();
    const role = newOpRole();
    if (!email || !name || password.length < 8) {
      setErr("Email, nom et mot de passe (8 caractères min) requis.");
      return;
    }
    run(() => jsend("/api/operators", "POST", { email, name, password, roles: [role] }), [operatorsQ]).then(() => {
      setNewOpEmail("");
      setNewOpName("");
      setNewOpPassword("");
    });
  }
  async function deleteOperator(id: string) {
    const op = operatorsQ.data?.find((o) => o.id === id);
    const label = op?.name || op?.email || id.slice(0, 8);
    const ok = await confirmDialog.confirm({
      title: "Supprimer cet opérateur",
      body: `Supprimer l'opérateur "${label}" ? Action irréversible : ses sessions actives seront fermées, ses permissions retirées.`,
      level: "danger",
      entityName: label,
      confirmLabel: "Supprimer",
    });
    if (!ok) return;
    void run(() => jsend(`/api/operators/${id}`, "DELETE"), [operatorsQ]);
  }
  async function generateInstallToken() {
    const tenantId = itTenant();
    if (!tenantId) {
      setErr("Sélectionnez un tenant pour générer un install token.");
      return;
    }
    if (itScope() === "client" && !itClient()) {
      setErr("Sélectionnez un client pour générer un token client.");
      return;
    }
    if (itScope() === "site" && (!itClient() || !itSite())) {
      setErr("Sélectionnez un client et un site pour générer un token site.");
      return;
    }
    const body: Record<string, unknown> = {
      label: itLabel().trim() || `console-${itScope()}`,
      ttlMinutes: Number(itTtl()) || 60,
      permanent: itPermanent(),
      usageMode: itUsageMode(),
    };
    if (itScope() === "client" || itScope() === "site") body.clientId = itClient();
    if (itScope() === "site") body.siteId = itSite();
    setErr(null);
    setItResult(null);
    try {
      const r = await jsend(`/tenants/${tenantId}/install-tokens`, "POST", body);
      setItResult({
        token: String(r.token || r.plainToken || ""),
        expiresAt: r.expiresAt ? String(r.expiresAt) : null,
        permanent: Boolean(r.permanent),
        usageMode: r.usageMode === "multiple" ? "multiple" : "single",
        installUrl: String(r.installUrl || ""),
        zipInstallUrl: String(r.zipInstallUrl || ""),
        linuxInstallUrl: String(r.linuxInstallUrl || ""),
        linuxCurl: String(r.linuxCurl || ""),
        windowsUpdaterUrl: String(r.windowsUpdaterUrl || ""),
        windowsPowerShell: String(r.windowsPowerShell || ""),
        macCurl: String(r.macCurl || ""),
      });
      void installTokensQ.refetch();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }

  function tokenStatus(t: InstallToken): { label: string; color: string; active: boolean } {
    if (t.revoked_at) return { label: "Révoqué", color: "var(--danger, #ef4444)", active: false };
    if (t.usage_mode !== "multiple" && t.used_at)
      return { label: "Utilisé", color: "var(--ok, #22c55e)", active: false };
    if (t.expires_at && Date.parse(t.expires_at) <= Date.now()) {
      return { label: "Expiré", color: "var(--text-3)", active: false };
    }
    return { label: "Actif", color: "var(--ok, #22c55e)", active: true };
  }

  async function showInstallInfo(token: InstallToken) {
    setErr(null);
    try {
      const r = await jget<Record<string, unknown>>(
        `/tenants/${token.tenant_id}/install-tokens/${token.id}/install-info`,
      );
      setItResult({
        token: String(r.token || r.installKey || ""),
        expiresAt: r.expiresAt ? String(r.expiresAt) : null,
        permanent: Boolean(r.permanent),
        usageMode: r.usageMode === "multiple" ? "multiple" : "single",
        installUrl: String(r.installUrl || ""),
        zipInstallUrl: String(r.zipInstallUrl || ""),
        linuxInstallUrl: String(r.linuxInstallUrl || ""),
        linuxCurl: String(r.linuxCurl || ""),
        windowsUpdaterUrl: String(r.windowsUpdaterUrl || ""),
        windowsPowerShell: String(r.windowsPowerShell || ""),
        macCurl: String(r.macCurl || ""),
      });
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }

  function tokenScope(t: InstallToken): string {
    if (t.site_id) return `Site: ${siteName(t.site_id)}`;
    if (t.client_id) return `Client: ${clientName(t.client_id)}`;
    return "Tenant";
  }

  async function revokeInstallToken(token: InstallToken) {
    const ok = await confirmDialog.confirm({
      title: "Révoquer l'install token",
      body: `Révoquer "${token.label || token.id.slice(0, 8)}" ? Le lien d'installation associé ne pourra plus enrôler de host.`,
      level: "danger",
      entityName: token.label || token.id,
      confirmLabel: "Révoquer",
    });
    if (!ok) return;
    await run(() => jsend(`/tenants/${token.tenant_id}/install-tokens/${token.id}`, "DELETE"), [installTokensQ]);
  }

  // ── Éditeur de stratégies (policies) ──
  // Modale hébergeant <PolicyEditor> pour le client ou le site sélectionné.
  const presetsQ = createQuery<Preset[]>(() => ({
    queryKey: ["host-policy-presets"],
    queryFn: () => jget<{ presets: Preset[] }>("/api/host-policy/presets").then((d) => d.presets || []),
  }));
  const [policyModal, setPolicyModal] = createSignal<{
    scope: "client" | "site";
    scopeId: string;
    scopeLabel: string;
    initial: PolicyObject;
  } | null>(null);

  function openPolicyForClient(c: Client) {
    setPolicyModal({
      scope: "client",
      scopeId: c.id,
      scopeLabel: c.name,
      initial: c.policy || {},
    });
  }
  function openPolicyForSite(s: Site, clientNameLabel: string) {
    setPolicyModal({
      scope: "site",
      scopeId: s.id,
      scopeLabel: `${clientNameLabel} / ${s.name}`,
      initial: s.policy || {},
    });
  }
  async function savePolicy(patch: PolicyObject) {
    const m = policyModal();
    if (!m) return;
    const url = m.scope === "client" ? `/api/clients/${m.scopeId}` : `/api/sites/${m.scopeId}`;
    await jsend(url, "PATCH", { policy: patch });
    await clientsQ.refetch();
    await sitesQ.refetch();
    setPolicyModal(null);
  }

  function hasCustomPolicy(p?: PolicyObject): boolean {
    return !!p && Object.keys(p).length > 0;
  }

  const inputStyle = {
    background: "var(--bg-2, #1e293b)",
    color: "var(--text-1, #e8ecf3)",
    border: "1px solid var(--border, #334155)",
    "border-radius": "4px",
    padding: "4px 8px",
    "font-size": "12px",
  } as const;

  return (
    <div class="plugins-console" style={{ overflow: "auto" }}>
      <div class="plugins-console-header">
        <span class="plugins-console-title">
          Clients &amp; RBAC
          <span class="plugins-console-sub">multi-tenant — délégation scopée</span>
        </span>
      </div>

      {/* ── Sous-navigation ───────────────────────────────────────── */}
      <div
        style={{
          display: "flex",
          gap: "2px",
          "border-bottom": "1px solid var(--border, #334155)",
          "margin-bottom": "12px",
          "overflow-x": "auto",
          "-webkit-overflow-scrolling": "touch",
        }}
      >
        <For each={SUB_TABS}>
          {(t) => (
            <button
              type="button"
              onClick={() => setTab(t.id)}
              style={{
                background: tab() === t.id ? "var(--bg-2, #1e293b)" : "transparent",
                color: tab() === t.id ? "var(--text-1, #e8ecf3)" : "var(--text-3, #94a3b8)",
                border: "none",
                "border-bottom": tab() === t.id ? "2px solid var(--accent, #38bdf8)" : "2px solid transparent",
                padding: "8px 14px",
                cursor: "pointer",
                "font-size": "13px",
                "font-weight": "500",
                "white-space": "nowrap",
              }}
            >
              {t.label}
            </button>
          )}
        </For>
      </div>

      <Show when={err()}>
        <div
          style={{
            background: "rgba(220,38,38,0.12)",
            border: "1px solid var(--danger, #dc2626)",
            color: "var(--danger, #f87171)",
            padding: "6px 10px",
            "border-radius": "4px",
            "font-size": "12px",
            "margin-bottom": "10px",
          }}
        >
          {err()}
        </div>
      </Show>

      <Show when={tab() === "clients"}>
        {/* ── Section Clients ─────────────────────────────────────────── */}
        <div class="rmm-table-wrap" style={{ "margin-bottom": "14px" }}>
          <div style={{ display: "flex", gap: "6px", padding: "8px", "flex-wrap": "wrap", "align-items": "center" }}>
            <input
              style={inputStyle}
              placeholder="Nom du nouveau client"
              value={newClientName()}
              onInput={(e) => setNewClientName(e.currentTarget.value)}
            />
            <select
              style={inputStyle}
              value={newClientTenant()}
              onChange={(e) => setNewClientTenant(e.currentTarget.value)}
            >
              <option value="">Tenant propriétaire…</option>
              <For each={tenantsQ.data || []}>{(t) => <option value={t.id}>{t.name}</option>}</For>
            </select>
            <button class="btn btn--primary btn--xs" type="button" onClick={createClient}>
              + Créer client
            </button>
            <div style={{ flex: "1 1 0" }} />
            <button class="btn btn--xs" type="button" onClick={() => clientsQ.refetch()}>
              ↺ Rafraîchir
            </button>
          </div>
          <table class="rmm-table">
            <thead>
              <tr>
                <th>Client</th>
                <th>Slug</th>
                <th>Tenant propriétaire</th>
                <th>Créé</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <Show
                when={(clientsQ.data || []).length > 0}
                fallback={
                  <tr>
                    <td colspan="5" style={{ color: "var(--text-4)", "text-align": "center", padding: "12px" }}>
                      Aucun client. Créez-en un ci-dessus.
                    </td>
                  </tr>
                }
              >
                <For each={clientsQ.data || []}>
                  {(c) => (
                    <tr
                      onClick={() => setSelectedClientId(c.id)}
                      style={{
                        cursor: "pointer",
                        background: selectedClientId() === c.id ? "rgba(56,189,248,0.10)" : undefined,
                      }}
                    >
                      <td style={{ color: "var(--text-1)", "font-weight": "500" }}>{c.name}</td>
                      <td style={{ "font-family": "var(--font-mono)", "font-size": "11px", color: "var(--text-4)" }}>
                        {c.slug}
                      </td>
                      <td>{tenantName(c.ownerTenantId)}</td>
                      <td style={{ "font-size": "11px", color: "var(--text-4)" }}>
                        {new Date(c.createdAt).toLocaleDateString()}
                      </td>
                      <td>
                        <Show when={hasCustomPolicy(c.policy)}>
                          <span class="tag" style={{ "margin-right": "4px" }} title="Stratégie personnalisée">
                            policy
                          </span>
                        </Show>
                        <button
                          class="btn btn--xs"
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            openPolicyForClient(c);
                          }}
                        >
                          Stratégie
                        </button>
                        <button
                          class="btn btn--xs btn--danger"
                          type="button"
                          style={{ "margin-left": "4px" }}
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteClient(c.id);
                          }}
                        >
                          Supprimer
                        </button>
                      </td>
                    </tr>
                  )}
                </For>
              </Show>
            </tbody>
          </table>
        </div>

        {/* ── Sites + Partages du client sélectionné ──────────────────── */}
        <Show when={selectedClient()}>
          {(client) => (
            <div style={{ display: "flex", gap: "14px", "flex-wrap": "wrap", "margin-bottom": "14px" }}>
              {/* Sites */}
              <div class="rmm-table-wrap" style={{ flex: "1 1 320px" }}>
                <div style={{ padding: "8px", "font-weight": "600", color: "var(--text-2)" }}>
                  Sites — {client().name}
                </div>
                <div style={{ display: "flex", gap: "6px", padding: "0 8px 8px" }}>
                  <input
                    style={inputStyle}
                    placeholder="Nom du site"
                    value={newSiteName()}
                    onInput={(e) => setNewSiteName(e.currentTarget.value)}
                  />
                  <button class="btn btn--primary btn--xs" type="button" onClick={createSite}>
                    + Site
                  </button>
                </div>
                <table class="rmm-table">
                  <tbody>
                    <Show
                      when={(sitesQ.data || []).length > 0}
                      fallback={
                        <tr>
                          <td colspan="2" style={{ color: "var(--text-4)", padding: "8px" }}>
                            Aucun site.
                          </td>
                        </tr>
                      }
                    >
                      <For each={sitesQ.data || []}>
                        {(s) => (
                          <tr>
                            <td style={{ color: "var(--text-1)" }}>
                              {s.name}
                              <Show when={hasCustomPolicy(s.policy)}>
                                <span class="tag" style={{ "margin-left": "6px" }} title="Stratégie personnalisée">
                                  policy
                                </span>
                              </Show>
                            </td>
                            <td style={{ "text-align": "right", "white-space": "nowrap" }}>
                              <button
                                class="btn btn--xs"
                                type="button"
                                style={{ "margin-right": "4px" }}
                                onClick={() => openPolicyForSite(s, selectedClient()?.name || "")}
                                title="Éditer la stratégie de ce site"
                              >
                                Stratégie
                              </button>
                              <button class="btn btn--xs btn--danger" type="button" onClick={() => deleteSite(s.id)}>
                                ✕
                              </button>
                            </td>
                          </tr>
                        )}
                      </For>
                    </Show>
                  </tbody>
                </table>
              </div>

              {/* Partages inter-tenants */}
              <div class="rmm-table-wrap" style={{ flex: "1 1 360px" }}>
                <div style={{ padding: "8px", "font-weight": "600", color: "var(--text-2)" }}>
                  Partages — tenants invités
                </div>
                <div style={{ display: "flex", gap: "6px", padding: "0 8px 8px", "flex-wrap": "wrap" }}>
                  <select
                    style={inputStyle}
                    value={shareTenant()}
                    onChange={(e) => setShareTenant(e.currentTarget.value)}
                  >
                    <option value="">Tenant invité…</option>
                    <For each={(tenantsQ.data || []).filter((t) => t.id !== client().ownerTenantId)}>
                      {(t) => <option value={t.id}>{t.name}</option>}
                    </For>
                  </select>
                  <select
                    style={inputStyle}
                    value={shareLevel()}
                    onChange={(e) => setShareLevel(e.currentTarget.value)}
                  >
                    <For each={ACCESS_LEVELS}>{(l) => <option value={l}>{l}</option>}</For>
                  </select>
                  <button class="btn btn--primary btn--xs" type="button" onClick={addShare}>
                    + Inviter
                  </button>
                </div>
                <table class="rmm-table">
                  <tbody>
                    <Show
                      when={(sharesQ.data || []).length > 0}
                      fallback={
                        <tr>
                          <td colspan="3" style={{ color: "var(--text-4)", padding: "8px" }}>
                            Aucun partage.
                          </td>
                        </tr>
                      }
                    >
                      <For each={sharesQ.data || []}>
                        {(sh) => (
                          <tr>
                            <td style={{ color: "var(--text-1)" }}>{tenantName(sh.tenantId)}</td>
                            <td>
                              <span class="tag">{sh.accessLevel}</span>
                            </td>
                            <td style={{ "text-align": "right" }}>
                              <button
                                class="btn btn--xs btn--danger"
                                type="button"
                                onClick={() => removeShare(sh.tenantId)}
                              >
                                ✕
                              </button>
                            </td>
                          </tr>
                        )}
                      </For>
                    </Show>
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </Show>
      </Show>

      <Show when={tab() === "operators"}>
        {/* ── Section Grants RBAC ─────────────────────────────────────── */}
        <div class="rmm-table-wrap">
          <div style={{ padding: "8px", "font-weight": "600", color: "var(--text-2)" }}>
            Grants RBAC — délégation d'accès opérateur
          </div>
          <div
            style={{ display: "flex", gap: "6px", padding: "0 8px 8px", "flex-wrap": "wrap", "align-items": "center" }}
          >
            <select
              style={{ ...inputStyle, "min-width": "200px" }}
              value={grantUser()}
              onChange={(e) => setGrantUser(e.currentTarget.value)}
            >
              <option value="">Opérateur…</option>
              <For each={operatorsQ.data || []}>{(op) => <option value={op.id}>{op.name || op.email}</option>}</For>
            </select>
            <select
              style={inputStyle}
              value={grantScopeType()}
              onChange={(e) => setGrantScopeType(e.currentTarget.value)}
            >
              <For each={SCOPE_TYPES}>{(s) => <option value={s}>{s}</option>}</For>
            </select>
            <Show
              when={grantScopeType() === "client"}
              fallback={
                <Show
                  when={grantScopeType() === "tenant"}
                  fallback={
                    <input
                      style={inputStyle}
                      placeholder={grantScopeType() === "host" ? "hostId" : "siteId"}
                      value={grantScopeId()}
                      onInput={(e) => setGrantScopeId(e.currentTarget.value)}
                    />
                  }
                >
                  <select
                    style={inputStyle}
                    value={grantScopeId()}
                    onChange={(e) => setGrantScopeId(e.currentTarget.value)}
                  >
                    <option value="">Tenant…</option>
                    <For each={tenantsQ.data || []}>{(t) => <option value={t.id}>{t.name}</option>}</For>
                  </select>
                </Show>
              }
            >
              <select
                style={inputStyle}
                value={grantScopeId()}
                onChange={(e) => setGrantScopeId(e.currentTarget.value)}
              >
                <option value="">Client…</option>
                <For each={clientsQ.data || []}>{(c) => <option value={c.id}>{c.name}</option>}</For>
              </select>
            </Show>
            <select style={inputStyle} value={grantRole()} onChange={(e) => setGrantRole(e.currentTarget.value)}>
              <For each={RBAC_ROLES}>{(r) => <option value={r}>{r}</option>}</For>
            </select>
            <button class="btn btn--primary btn--xs" type="button" onClick={createGrant}>
              + Accorder
            </button>
          </div>
          <table class="rmm-table">
            <thead>
              <tr>
                <th>Opérateur</th>
                <th>Scope</th>
                <th>Cible</th>
                <th>Rôle</th>
                <th>Expire</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <Show
                when={(grantsQ.data || []).length > 0}
                fallback={
                  <tr>
                    <td colspan="6" style={{ color: "var(--text-4)", "text-align": "center", padding: "12px" }}>
                      Aucun grant.
                    </td>
                  </tr>
                }
              >
                <For each={grantsQ.data || []}>
                  {(g) => (
                    <tr>
                      <td style={{ "font-size": "11px" }}>{operatorName(g.userId)}</td>
                      <td>
                        <span class="tag">{g.scopeType}</span>
                      </td>
                      <td style={{ "font-size": "11px" }}>
                        {g.scopeType === "client"
                          ? clientName(g.scopeId)
                          : g.scopeType === "tenant"
                            ? tenantName(g.scopeId)
                            : g.scopeId}
                      </td>
                      <td>
                        <span class="tag">{g.role}</span>
                      </td>
                      <td style={{ "font-size": "11px", color: "var(--text-4)" }}>
                        {g.expiresAt ? new Date(g.expiresAt).toLocaleDateString() : "—"}
                      </td>
                      <td>
                        <button class="btn btn--xs btn--danger" type="button" onClick={() => deleteGrant(g.id)}>
                          Révoquer
                        </button>
                      </td>
                    </tr>
                  )}
                </For>
              </Show>
            </tbody>
          </table>
        </div>
        {/* ── Section Opérateurs → tenant ─────────────────────────────── */}
        <div class="rmm-table-wrap" style={{ "margin-top": "14px" }}>
          <div style={{ padding: "8px", "font-weight": "600", color: "var(--text-2)" }}>
            Opérateurs — provisioning + affectation tenant
          </div>
          <div
            style={{ display: "flex", gap: "6px", padding: "0 8px 8px", "flex-wrap": "wrap", "align-items": "center" }}
          >
            <input
              style={inputStyle}
              placeholder="Email"
              value={newOpEmail()}
              onInput={(e) => setNewOpEmail(e.currentTarget.value)}
            />
            <input
              style={inputStyle}
              placeholder="Nom"
              value={newOpName()}
              onInput={(e) => setNewOpName(e.currentTarget.value)}
            />
            <input
              style={inputStyle}
              type="password"
              placeholder="Mot de passe (8 car. min)"
              value={newOpPassword()}
              onInput={(e) => setNewOpPassword(e.currentTarget.value)}
            />
            <select style={inputStyle} value={newOpRole()} onChange={(e) => setNewOpRole(e.currentTarget.value)}>
              <option value="support">support</option>
              <option value="admin">admin</option>
              <option value="auditor">auditor</option>
            </select>
            <button class="btn btn--primary btn--xs" type="button" onClick={createOperator}>
              + Créer opérateur
            </button>
          </div>
          <table class="rmm-table">
            <thead>
              <tr>
                <th>Opérateur</th>
                <th>Rôles</th>
                <th>Tenant</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <Show
                when={(operatorsQ.data || []).length > 0}
                fallback={
                  <tr>
                    <td colspan="4" style={{ color: "var(--text-4)", padding: "10px" }}>
                      Aucun opérateur.
                    </td>
                  </tr>
                }
              >
                <For each={operatorsQ.data || []}>
                  {(op) => (
                    <tr>
                      <td>
                        <span style={{ color: "var(--text-1)" }}>{op.name || op.email}</span>
                        <br />
                        <span style={{ "font-size": "10px", color: "var(--text-4)" }}>{op.email}</span>
                      </td>
                      <td>
                        <For each={op.roles}>
                          {(r) => (
                            <span class="tag" style={{ "margin-right": "3px" }}>
                              {r}
                            </span>
                          )}
                        </For>
                      </td>
                      <td>
                        <select
                          style={inputStyle}
                          value={op.tenantId || ""}
                          onChange={(e) => assignOperatorTenant(op.id, e.currentTarget.value, op.tenantId)}
                        >
                          <option value="">— Plateforme (aucun tenant) —</option>
                          <For each={tenantsQ.data || []}>{(t) => <option value={t.id}>{t.name}</option>}</For>
                        </select>
                      </td>
                      <td style={{ "text-align": "right" }}>
                        <button class="btn btn--xs btn--danger" type="button" onClick={() => deleteOperator(op.id)}>
                          ✕
                        </button>
                      </td>
                    </tr>
                  )}
                </For>
              </Show>
            </tbody>
          </table>
        </div>
      </Show>

      <Show when={tab() === "assignments"}>
        {/* ── Section Hosts → client / site ───────────────────────────── */}
        <div class="rmm-table-wrap" style={{ "margin-top": "14px" }}>
          <div style={{ padding: "8px", "font-weight": "600", color: "var(--text-2)" }}>
            Hosts — affectation client / site
          </div>
          <table class="rmm-table">
            <thead>
              <tr>
                <th>Host</th>
                <th>Client</th>
                <th>Site</th>
              </tr>
            </thead>
            <tbody>
              <Show
                when={(hostsQ.data || []).length > 0}
                fallback={
                  <tr>
                    <td colspan="3" style={{ color: "var(--text-4)", padding: "10px" }}>
                      Aucun host.
                    </td>
                  </tr>
                }
              >
                <For each={hostsQ.data || []}>
                  {(h) => (
                    <tr>
                      <td style={{ color: "var(--text-1)" }}>
                        <div>{h.hostname || h.hostId}</div>
                        <code style={{ color: "var(--text-3)", "font-size": "11px" }}>{h.hostId}</code>
                        <Show when={h.status === "pending"}>
                          <span class="badge badge--warn" style={{ "margin-left": "6px" }}>
                            en attente
                          </span>
                        </Show>
                      </td>
                      <td>
                        <select
                          style={inputStyle}
                          value={h.clientId || ""}
                          onChange={(e) => assignHostClient(h.hostId, e.currentTarget.value)}
                        >
                          <option value="">— Aucun —</option>
                          <For each={clientsQ.data || []}>{(c) => <option value={c.id}>{c.name}</option>}</For>
                        </select>
                      </td>
                      <td>
                        <select
                          style={inputStyle}
                          value={h.siteId || ""}
                          disabled={!h.clientId}
                          onChange={(e) => assignHostSite(h.hostId, e.currentTarget.value)}
                        >
                          <option value="">— Aucun —</option>
                          <For each={(allSitesQ.data || []).filter((s) => s.clientId === h.clientId)}>
                            {(s) => <option value={s.id}>{s.name}</option>}
                          </For>
                        </select>
                      </td>
                    </tr>
                  )}
                </For>
              </Show>
            </tbody>
          </table>
        </div>
      </Show>

      <Show when={tab() === "tokens"}>
        {/* ── Section Install tokens ──────────────────────────────────── */}
        <div class="rmm-table-wrap" style={{ "margin-top": "14px" }}>
          <div style={{ padding: "8px", "font-weight": "600", color: "var(--text-2)" }}>
            Install token — enrôlement scopé (tenant / client / site)
          </div>
          <div
            style={{ display: "flex", gap: "6px", padding: "0 8px 8px", "flex-wrap": "wrap", "align-items": "center" }}
          >
            <select
              style={inputStyle}
              value={itTenant()}
              onChange={(e) => {
                setItTenant(e.currentTarget.value);
                setItClient("");
                setItSite("");
              }}
            >
              <option value="">Tenant…</option>
              <For each={tenantsQ.data || []}>{(t) => <option value={t.id}>{t.name}</option>}</For>
            </select>
            <select
              style={inputStyle}
              value={itScope()}
              disabled={!itTenant()}
              onChange={(e) => {
                const next = e.currentTarget.value as "general" | "client" | "site";
                setItScope(next);
                if (next === "general") {
                  setItClient("");
                  setItSite("");
                }
                if (next === "client") setItSite("");
              }}
              title="Type de token"
            >
              <option value="general">Général tenant (non-assignés)</option>
              <option value="client">Client</option>
              <option value="site">Client + site</option>
            </select>
            <select
              style={inputStyle}
              value={itClient()}
              disabled={!itTenant() || itScope() === "general"}
              onChange={(e) => {
                setItClient(e.currentTarget.value);
                setItSite("");
              }}
            >
              <option value="">Client…</option>
              <For each={(clientsQ.data || []).filter((c) => c.ownerTenantId === itTenant())}>
                {(c) => <option value={c.id}>{c.name}</option>}
              </For>
            </select>
            <select
              style={inputStyle}
              value={itSite()}
              disabled={!itClient() || itScope() !== "site"}
              onChange={(e) => setItSite(e.currentTarget.value)}
            >
              <option value="">Site…</option>
              <For each={(allSitesQ.data || []).filter((s) => s.clientId === itClient())}>
                {(s) => <option value={s.id}>{s.name}</option>}
              </For>
            </select>
            <input
              style={inputStyle}
              placeholder="Libellé"
              value={itLabel()}
              onInput={(e) => setItLabel(e.currentTarget.value)}
            />
            <input
              style={{ ...inputStyle, width: "75px" }}
              type="number"
              placeholder="TTL min"
              value={itTtl()}
              disabled={itPermanent()}
              onInput={(e) => setItTtl(e.currentTarget.value)}
            />
            <label style={{ display: "inline-flex", "align-items": "center", gap: "4px", "font-size": "12px" }}>
              <input
                type="checkbox"
                checked={itPermanent()}
                onChange={(e) => setItPermanent(e.currentTarget.checked)}
              />
              Permanent
            </label>
            <select
              style={inputStyle}
              value={itUsageMode()}
              onChange={(e) => setItUsageMode(e.currentTarget.value === "multiple" ? "multiple" : "single")}
              title="Nombre d'installations autorisées"
            >
              <option value="single">Une install</option>
              <option value="multiple">Multiple installs</option>
            </select>
            <button class="btn btn--primary btn--xs" type="button" onClick={generateInstallToken}>
              Générer
            </button>
          </div>
          <Show when={itResult()}>
            {(r) => (
              <div
                style={{
                  background: "rgba(34,197,94,0.08)",
                  border: "1px solid var(--ok, #22c55e)",
                  padding: "8px",
                  margin: "0 8px 8px",
                  "border-radius": "4px",
                }}
              >
                <div style={{ "font-size": "11px", color: "var(--text-3)", "margin-bottom": "4px" }}>
                  Lien d'installation (à envoyer au client) —{" "}
                  {r().permanent || !r().expiresAt
                    ? "permanent"
                    : `expire le ${new Date(r().expiresAt!).toLocaleString()}`}{" "}
                  — {r().usageMode === "multiple" ? "multi-install" : "une install"}
                </div>
                <div style={{ "font-size": "11px", color: "var(--text-3)", "margin-bottom": "4px" }}>
                  Téléchargement Windows updater par double-clic
                </div>
                <div style={{ display: "flex", gap: "6px", "align-items": "center", "margin-bottom": "6px" }}>
                  <code
                    style={{
                      flex: "1 1 0",
                      "font-family": "var(--font-mono)",
                      "font-size": "11px",
                      background: "var(--bg-2, #1e293b)",
                      padding: "4px 6px",
                      "border-radius": "3px",
                      overflow: "auto",
                      "white-space": "nowrap",
                    }}
                  >
                    {r().windowsUpdaterUrl || r().installUrl}
                  </code>
                  <button
                    class="btn btn--xs"
                    type="button"
                    onClick={() => navigator.clipboard?.writeText(r().windowsUpdaterUrl || r().installUrl)}
                  >
                    Copier lien
                  </button>
                </div>
                <Show when={r().zipInstallUrl}>
                  <div style={{ "font-size": "11px", color: "var(--text-3)", "margin-bottom": "4px" }}>
                    ZIP Windows legacy
                  </div>
                  <div style={{ display: "flex", gap: "6px", "align-items": "center", "margin-bottom": "6px" }}>
                    <code
                      style={{
                        flex: "1 1 0",
                        "font-family": "var(--font-mono)",
                        "font-size": "11px",
                        background: "var(--bg-2, #1e293b)",
                        padding: "4px 6px",
                        "border-radius": "3px",
                        overflow: "auto",
                        "white-space": "nowrap",
                      }}
                    >
                      {r().zipInstallUrl}
                    </code>
                    <button
                      class="btn btn--xs"
                      type="button"
                      onClick={() => navigator.clipboard?.writeText(r().zipInstallUrl)}
                    >
                      Copier ZIP
                    </button>
                  </div>
                </Show>
                <Show when={r().linuxCurl}>
                  <div style={{ "font-size": "11px", color: "var(--text-3)", "margin-bottom": "4px" }}>
                    Installation Ubuntu/Linux par curl
                  </div>
                  <div style={{ display: "flex", gap: "6px", "align-items": "center", "margin-bottom": "6px" }}>
                    <code
                      style={{
                        flex: "1 1 0",
                        "font-family": "var(--font-mono)",
                        "font-size": "11px",
                        background: "var(--bg-2, #1e293b)",
                        padding: "4px 6px",
                        "border-radius": "3px",
                        overflow: "auto",
                        "white-space": "nowrap",
                      }}
                    >
                      {r().linuxCurl}
                    </code>
                    <button
                      class="btn btn--xs"
                      type="button"
                      onClick={() => navigator.clipboard?.writeText(r().linuxCurl)}
                    >
                      Copier curl
                    </button>
                  </div>
                </Show>
                <Show when={r().windowsPowerShell}>
                  <div style={{ "font-size": "11px", color: "var(--text-3)", "margin-bottom": "4px" }}>
                    Installation Windows interactive par PowerShell (token prérempli, x64/ARM64)
                  </div>
                  <div style={{ display: "flex", gap: "6px", "align-items": "center", "margin-bottom": "6px" }}>
                    <code
                      style={{
                        flex: "1 1 0",
                        "font-family": "var(--font-mono)",
                        "font-size": "11px",
                        background: "var(--bg-2, #1e293b)",
                        padding: "4px 6px",
                        "border-radius": "3px",
                        overflow: "auto",
                        "white-space": "nowrap",
                      }}
                    >
                      {r().windowsPowerShell}
                    </code>
                    <button
                      class="btn btn--xs"
                      type="button"
                      onClick={() => navigator.clipboard?.writeText(r().windowsPowerShell)}
                    >
                      Copier PowerShell
                    </button>
                  </div>
                </Show>
                <Show when={r().macCurl}>
                  <div style={{ "font-size": "11px", color: "var(--text-3)", "margin-bottom": "4px" }}>
                    Installation macOS par curl
                  </div>
                  <div style={{ display: "flex", gap: "6px", "align-items": "center", "margin-bottom": "6px" }}>
                    <code
                      style={{
                        flex: "1 1 0",
                        "font-family": "var(--font-mono)",
                        "font-size": "11px",
                        background: "var(--bg-2, #1e293b)",
                        padding: "4px 6px",
                        "border-radius": "3px",
                        overflow: "auto",
                        "white-space": "nowrap",
                      }}
                    >
                      {r().macCurl}
                    </code>
                    <button
                      class="btn btn--xs"
                      type="button"
                      onClick={() => navigator.clipboard?.writeText(r().macCurl)}
                    >
                      Copier macOS
                    </button>
                  </div>
                </Show>
                <div style={{ display: "flex", gap: "6px", "align-items": "center" }}>
                  <code
                    style={{
                      flex: "1 1 0",
                      "font-family": "var(--font-mono)",
                      "font-size": "11px",
                      background: "var(--bg-2, #1e293b)",
                      padding: "4px 6px",
                      "border-radius": "3px",
                      overflow: "auto",
                      "white-space": "nowrap",
                    }}
                  >
                    {r().token}
                  </code>
                  <button class="btn btn--xs" type="button" onClick={() => navigator.clipboard?.writeText(r().token)}>
                    Copier token
                  </button>
                  <button class="btn btn--xs" type="button" onClick={() => setItResult(null)}>
                    Masquer
                  </button>
                </div>
              </div>
            )}
          </Show>
          <div style={{ padding: "0 8px 8px" }}>
            <div
              style={{
                display: "flex",
                "align-items": "center",
                "justify-content": "space-between",
                gap: "8px",
                "margin-bottom": "6px",
              }}
            >
              <span style={{ "font-size": "12px", color: "var(--text-2)" }}>
                Tokens créés {itTenant() ? `— ${tenantName(itTenant())}` : ""}
              </span>
              <button class="btn btn--xs" type="button" disabled={!itTenant()} onClick={() => installTokensQ.refetch()}>
                Rafraîchir
              </button>
            </div>
            <Show
              when={itTenant()}
              fallback={
                <div style={{ color: "var(--text-3)", "font-size": "12px", padding: "8px 0" }}>
                  Sélectionnez un tenant pour revoir ses tokens.
                </div>
              }
            >
              <table class="rmm-table">
                <thead>
                  <tr>
                    <th>Statut</th>
                    <th>Libellé</th>
                    <th>Scope</th>
                    <th>Mode</th>
                    <th>Créé</th>
                    <th>Expire</th>
                    <th>Utilisé par</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <Show
                    when={(installTokensQ.data || []).length > 0}
                    fallback={
                      <tr>
                        <td colspan="8" style={{ color: "var(--text-3)" }}>
                          Aucun token.
                        </td>
                      </tr>
                    }
                  >
                    <For each={installTokensQ.data || []}>
                      {(t) => {
                        const status = () => tokenStatus(t);
                        return (
                          <tr>
                            <td>
                              <span style={{ color: status().color, "font-weight": 600 }}>{status().label}</span>
                            </td>
                            <td>{t.label || "—"}</td>
                            <td>{tokenScope(t)}</td>
                            <td>
                              {t.usage_mode === "multiple" ? "Multiple" : "Une install"}
                              {t.use_count ? ` (${t.use_count})` : ""}
                            </td>
                            <td>{new Date(t.created_at).toLocaleString()}</td>
                            <td>{t.expires_at ? new Date(t.expires_at).toLocaleString() : "Permanent"}</td>
                            <td>{t.used_by_host_id || "—"}</td>
                            <td style={{ "text-align": "right" }}>
                              <button
                                class="btn btn--xs"
                                type="button"
                                disabled={!t.install_key}
                                onClick={() => showInstallInfo(t)}
                                style={{ "margin-right": "6px" }}
                              >
                                Voir install
                              </button>
                              <button
                                class="btn btn--danger btn--xs"
                                type="button"
                                disabled={!!t.revoked_at}
                                onClick={() => revokeInstallToken(t)}
                              >
                                Révoquer
                              </button>
                            </td>
                          </tr>
                        );
                      }}
                    </For>
                  </Show>
                </tbody>
              </table>
            </Show>
          </div>
        </div>
      </Show>

      {/* ── Modale éditeur de stratégies (client / site) ──────────────── */}
      <Show when={policyModal()}>
        {(m) => (
          <div
            style={{
              position: "fixed",
              inset: "0",
              background: "rgba(0,0,0,0.55)",
              "z-index": "999",
              display: "flex",
              "align-items": "flex-start",
              "justify-content": "center",
              overflow: "auto",
              padding: "24px 12px",
            }}
            onClick={() => setPolicyModal(null)}
          >
            <div
              style={{
                background: "var(--bg-1, #131517)",
                border: "1px solid var(--border, #2a2e34)",
                "border-radius": "8px",
                width: "min(820px, 96vw)",
                "max-height": "92vh",
                overflow: "auto",
                padding: "16px",
                "box-shadow": "0 8px 32px rgba(0,0,0,0.4)",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <PolicyEditor
                scopeType={m().scope}
                scopeId={m().scopeId}
                scopeLabel={m().scopeLabel}
                initialPolicy={m().initial}
                presets={presetsQ.data || []}
                onSave={savePolicy}
                onCancel={() => setPolicyModal(null)}
              />
            </div>
          </div>
        )}
      </Show>
    </div>
  );
}
